import React, { useState, useEffect } from 'react';
import { api } from '../../../services/apiServices'; 
import { Button } from '../../../components/ui/button'; // 
import { Input } from '../../../components/ui/input'; // 
import { Badge } from '../../../components/ui/badge'; // 
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion'; 

export default function SearchTermsTab({ category, locale }) {
  const [searchTerms, setSearchTerms] = useState([]);
  const [masterTerms, setMasterTerms] = useState([]);
  const [newTerm, setNewTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (category?.id && locale) {
      fetchSearchTerms();
      
      // Only fetch master terms if we are looking at a local category
      if (locale !== "master") {
        fetchMasterTerms();
      }
    }
  }, [category?.id, locale]);

  const fetchSearchTerms = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/admin/categories/${category.id}/search-terms?locale=${locale}`);
      setSearchTerms(response.data);
    } catch (error) {
      console.error("Failed to fetch search terms", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMasterTerms = async () => {
    try {
      const response = await api.get(`/admin/categories/${category.id}/master-search-terms`);
      setMasterTerms(response.data);
    } catch (error) {
      console.error("Failed to fetch master search terms", error);
    }
  };

  const handleAddTerm = async () => {
    if (!newTerm.trim()) return;
    try {
      await api.post(`/admin/categories/${category.id}/search-terms?locale=${locale}`, {
        search_term: newTerm,
        is_active: true
      });
      setNewTerm('');
      fetchSearchTerms(); 
    } catch (error) {
      console.error("Failed to add search term", error);
    }
  };

  const handleDeleteTerm = async (termId) => {
    if (!window.confirm("Are you sure you want to delete this term?")) return;
    try {
      await api.delete(`/admin/categories/search-terms/${termId}?locale=${locale}`);
      fetchSearchTerms(); 
    } catch (error) {
      console.error("Failed to delete search term", error);
    }
  };

  const handleToggleActive = async (term) => {
    try {
      await api.put(`/admin/categories/search-terms/${term.id}?locale=${locale}`, {
        is_active: !term.is_active
      });
      fetchSearchTerms();
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  if (!category || !category.id) return <p>Please save the category first to manage search terms.</p>;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Manage Scraping Search Terms</h3>

      {/* Read-Only Master Terms Accordion (Only shows for local categories) */}
      {locale !== "master" && (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="master-terms">
            <AccordionTrigger className="text-sm font-medium text-gray-700">
              View Master Category Search Terms (Read-Only)
            </AccordionTrigger>
            <AccordionContent>
              {masterTerms.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No master search terms configured.</p>
              ) : (
                <div className="flex flex-wrap gap-2 pt-2">
                  {masterTerms.map(term => (
                    <Badge 
                      key={term.id} 
                      variant="secondary" 
                      className={!term.is_active ? 'opacity-50 line-through' : ''}
                    >
                      {term.search_term}
                    </Badge>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      
      {/* Add New Term */}
      <div className="flex gap-4 items-center">
        <Input 
          placeholder="e.g., Best Wedding Photographers" 
          value={newTerm}
          onChange={(e) => setNewTerm(e.target.value)}
          className="max-w-md"
        />
        <Button onClick={handleAddTerm} disabled={!newTerm.trim() || isLoading}>
          Add Term
        </Button>
      </div>

      {/* List Terms */}
      <div className="border rounded-md p-4">
        {isLoading ? (
          <p>Loading...</p>
        ) : searchTerms.length === 0 ? (
          <p className="text-gray-500">No search terms found for this locale. Add one above.</p>
        ) : (
          <ul className="space-y-3">
            {searchTerms.map(term => (
              <li key={term.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                <span className={!term.is_active ? 'line-through text-gray-400' : ''}>
                  {term.search_term}
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant={term.is_active ? "outline" : "default"} 
                    size="sm" 
                    onClick={() => handleToggleActive(term)}
                  >
                    {term.is_active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => handleDeleteTerm(term.id)}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}